export interface CalendarEventSelectionIdentity {
  eventId: number;
  settlementId: number;
  eventDate: string;
  eventTypeId: number;
}

export interface CalendarEventSelectionCandidate extends CalendarEventSelectionIdentity {
  settlementName: string;
  unitId: number | null;
  selectable: boolean;
}

export interface CalendarEventSelectionGroup {
  settlementName: string;
  eventIds: number[];
  unitCount: number;
}

export type CalendarEventSelectionMismatch = 'settlement' | 'date' | 'eventType';

export function getCalendarEventSelectionMismatch(
  selectedEvent: CalendarEventSelectionIdentity,
  candidateEvent: CalendarEventSelectionIdentity
): CalendarEventSelectionMismatch | null {
  if (selectedEvent.settlementId !== candidateEvent.settlementId) {
    return 'settlement';
  }

  if (selectedEvent.eventDate !== candidateEvent.eventDate) {
    return 'date';
  }

  if (selectedEvent.eventTypeId !== candidateEvent.eventTypeId) {
    return 'eventType';
  }

  return null;
}

export function isValidCalendarEventSelection(
  events: CalendarEventSelectionIdentity[]
): boolean {
  if (events.length === 0) {
    return false;
  }

  return events.slice(1).every(event =>
    getCalendarEventSelectionMismatch(events[0], event) === null
  );
}

export function getCalendarEventSelectionGroup(
  anchor: CalendarEventSelectionIdentity,
  candidates: CalendarEventSelectionCandidate[]
): CalendarEventSelectionGroup | null {
  const matchingCandidates = candidates.filter(candidate =>
    candidate.selectable
    && getCalendarEventSelectionMismatch(anchor, candidate) === null
  );
  if (matchingCandidates.length === 0) {
    return null;
  }

  const eventIds = [...new Set(matchingCandidates.map(candidate => candidate.eventId))];
  const unitKeys = new Set(matchingCandidates.map(candidate =>
    typeof candidate.unitId === 'number' && Number.isFinite(candidate.unitId)
      ? `unit:${candidate.unitId}`
      : `event:${candidate.eventId}`
  ));
  const settlementName = matchingCandidates
    .find(candidate => candidate.settlementName.trim())
    ?.settlementName.trim() ?? '';

  return {
    settlementName,
    eventIds,
    unitCount: unitKeys.size
  };
}
